import { License, User } from '@/models/gen/graphql';

import { FormEventHandler } from 'react';
import { ImageInputProps } from '@/common/ImageInput';
import { ModalTypes } from '@/common/Modal/types';
import { Schema, hasWhitespace } from '@/utils';
import { SubmitHandler } from 'react-hook-form';

export namespace UserModalTypes {
  export type BaseProps = ModalTypes.Props;
  export type InheritedProps = Omit<BaseProps, 'title' | 'content'>;
  export type Props = InheritedProps & {
    userIds: (string | null | undefined)[];
    onSubmit: SubmitHandler<User> & FormEventHandler<HTMLFormElement>;
    onHide: () => void;
  };
  export enum Tabs {
    PROFILE = 'PROFILE',
    TRAINING = 'TRAINING',
    LICENSES = 'LICENSES',
  }
  export type AvatarProps = Omit<ImageInputProps, 'onChange'> & {
    userId?: string;
  };
  export type InfoProps = {
    createdAt?: string;
    licenses?: License[];
    airports?: string[];
  };
  export type ProfileFormProps = {
    takenUsername?: string;
    onUsernameBlur?: (username: string) => void;
  };
  export type TitleProps = {
    firstName?: string;
    lastName?: string;
  };
  export const Validator = (driverRoleId: string, takenUsername?: string) =>
    Schema((z) => {
      const passwordSchema = z
        .object({
          id: z.string().optional(),
          password: z
            .string()
            .refine((val: string): boolean => !hasWhitespace(val), { message: 'Password cannot contain spaces' })
            .optional(),
          confirmPassword: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          if (data.id) return;
          if (!data.password) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Password is required',
              path: ['password'],
            });
          }
          if (!data.confirmPassword) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Confirm Password is required',
              path: ['confirmPassword'],
            });
          } else if (data.password !== data.confirmPassword) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Passwords do not match',
              path: ['confirmPassword'],
            });
          }
        });

      const profileSchema = z.object({
        username: z
          .string()
          .min(1, { message: 'Username is required' })
          .refine((val: string): boolean => !hasWhitespace(val), { message: 'Username cannot contain spaces' })
          .refine((val: string): boolean => val !== takenUsername, { message: 'Username is already taken' }),
        email: z.string().email({ message: 'Email is invalid' }),
        firstName: z.string().min(1, { message: 'First Name is required' }),
        lastName: z.string().min(1, { message: 'Last Name is required' }),
        phone: z.string().refine((value) => /^\d{10}$/.test(value.replace(/\D/g, '')), { message: 'Phone number is invalid' }),
        active: z.number({ message: 'Active is required' }),
        companyId: z.string().min(1, { message: 'Company is required' }),
        primaryAirportCode: z.string().optional(),
        address: z.string().optional(),
        city: z.string().optional(),
        state: z.string().optional(),
        zipCode: z.string().optional(),
        dob: z.string().optional(),
        startDate: z.string().optional(),
        notes: z.string().optional(),
        longTrip: z.union([z.boolean(), z.number()]).optional(),
      });

      const roleSchema = z
        .object({
          roleId: z.string().min(1, { message: 'Type is required' }),
          licenses: z
            .array(
              z
                .object({
                  number: z.string().optional(),
                  expirationDate: z.string().optional(),
                  stateCode: z.string().optional(),
                  licenseTypeId: z.string().optional(),
                })
                .optional()
            )
            .optional()
            .default([]),
        })
        .superRefine((data, ctx) => {
          if (data.roleId === driverRoleId) {
            if (!data.licenses || data.licenses.filter(Boolean).length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'At least one license is required for drivers',
                path: ['roleId'],
              });
            } else {
              data.licenses.filter(Boolean).forEach((license, index) => {
                if (!license.number) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'License Number is required',
                    path: ['licenses', index, 'number'],
                  });
                }
                if (!license.expirationDate) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Expiration Date is required',
                    path: ['licenses', index, 'expirationDate'],
                  });
                }
                if (!license.stateCode) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'State is required',
                    path: ['licenses', index, 'stateCode'],
                  });
                }
                if (!license.licenseTypeId) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'License Type is required',
                    path: ['licenses', index, 'licenseTypeId'],
                  });
                }
              });
            }
          }
        });

      const primaryAirportSchema = z
        .object({
          roleId: z.string().optional(),
          primaryAirportCode: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          if (data.roleId === driverRoleId && !data.primaryAirportCode) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Primary Airport is required',
              path: ['primaryAirportCode'],
            });
          }
        });

      return z.intersection(z.intersection(z.intersection(profileSchema, roleSchema), passwordSchema), primaryAirportSchema);
    });
}
